// NOTE: price display in product card
'use client'

import { generateTagTrackingValue } from '@/components/common/product-card/helper'
import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { cn, formatPrice } from '@/lib/utils'
import { stringToSlug } from '@/lib/utils/common'
import { ERankingLevel } from '@/types/auth'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'
import { useProductCardContext } from './context/product-card-context'
import { ProductItemRating } from './product-item-rating'
import { ProductItemVariant } from './product-item-variant'
import { ProductItemVariantMobile } from './product-item-variant-mobile'

const RANKING_ID_TO_LABEL: Record<string, string> = {
  [ERankingLevel.NEW]: 'new_user',
  [ERankingLevel.SILVER]: 'silver',
  [ERankingLevel.GOLD]: 'gold',
  [ERankingLevel.PLATINUM]: 'platinum',
}

// Thứ tự rank từ thấp đến cao
const RANKING_ORDER = [
  ERankingLevel.NEW,
  ERankingLevel.SILVER,
  ERankingLevel.GOLD,
  ERankingLevel.PLATINUM,
]

/**
 * Hiển thị giá sản phẩm, ưu tiên giá campaign nếu đang hoạt động.
 * Khi đang loading giá campaign, hiển thị skeleton.
 */
export const ProductItemInfo = ({
  gaTrackingValue,
  gaTrackingLabel,
  disableNavigation,
}: {
  gaTrackingValue?: string
  gaTrackingLabel?: string
  disableNavigation?: boolean
}) => {
  const { product, trackingClickProduct, selectedColor } = useProductCardContext()
  const isMobile = useIsMobileClient()
  const { productData, isCoolClubSale } = useCoolClubPrice(product)
  const t = useTranslations('product_detail')

  // Auto-generate tracking value from product tags if not provided
  const finalTrackingValue = product?.is_setdo
    ? 'product__listing_set-do'
    : gaTrackingValue || generateTagTrackingValue(product.tags)

  // Lấy giá campaign (nếu có), kèm trạng thái loading
  const {
    finalPrice: campaignPrice,
    originalPrice: campaignOriginalPrice,
    discountPercent: campaignDiscountPercent,
    shouldShowDiscount: campaignShouldShowDiscount,
    isLoading: isCampaignLoading,
  } = useCampaignPrice({
    regularPrice: product?.regular_price ?? 0,
    comparePrice: product?.compare_price ?? 0,
    productId: product.id,
    product: product,
  })

  // Xác định giá hiển thị dựa trên campaign hoặc giá gốc
  const getDisplayPrice = () => {
    if (campaignShouldShowDiscount) return campaignPrice
    return productData?.regular_price ?? 0
  }

  const getDisplayComparePrice = () => {
    if (campaignShouldShowDiscount && campaignOriginalPrice !== campaignPrice) {
      return campaignOriginalPrice
    }
    if (
      productData?.compare_price &&
      productData.compare_price !== productData.regular_price
    ) {
      return productData.compare_price
    }
    return undefined
  }

  const getDisplayDiscountPercent = () => {
    if (campaignShouldShowDiscount && campaignDiscountPercent > 0) {
      return campaignDiscountPercent
    }
    return productData?.sale_percent
  }

  const displayPrice = getDisplayPrice()
  const displayComparePrice = getDisplayComparePrice()
  const displayDiscountPercent = getDisplayDiscountPercent()

  const handleClick = () => {
    if (!disableNavigation) {
      trackingClickProduct()
    }
  }

  return (
    <figcaption className='flex flex-col gap-1 lg:gap-2'>
      <div className='flex w-full items-center gap-1.5 lg:gap-2'>
        <div className='min-w-0 flex-1'>
          {isMobile ? <ProductItemVariantMobile /> : <ProductItemVariant />}
        </div>
        <ProductItemRating />
      </div>

      <ProductTitleLink
        href={productData?.href ? `${productData.href}${selectedColor ? `?color=${stringToSlug(selectedColor)}` : ''}` : undefined}
        title={productData?.title}
        onClick={handleClick}
        gaTrackingValue={finalTrackingValue}
        gaTrackingLabel={gaTrackingLabel}
        disableNavigation={disableNavigation}
      />

      <ProductPriceBlock
        isLoading={isCampaignLoading}
        isCoolClubSale={isCoolClubSale}
        displayPrice={displayPrice}
        displayDiscountPercent={displayDiscountPercent}
        displayComparePrice={displayComparePrice}
      />

      {productData?.coolclub_sale?.active && !isCoolClubSale && (
        <CoolClubPriceLabel
          rankings={productData.coolclub_sale.rankings}
          price={productData.coolclub_sale.coolclub_price}
        />
      )}

      {productData?.note && (
        <p className='line-clamp-2 font-sans text-xs italic leading-3.5 text-neutral-500 lg:text-sm lg:leading-4.5'>
          {productData.note}
        </p>
      )}

      {/* Mô tả ẩn cho screen reader, hiển thị đúng giá theo campaign nếu có */}
      <span className='sr-only'>
        {`${t('sr.product', {
          title: productData?.title ?? '',
          price: isCampaignLoading ? '...' : formatPrice(displayPrice),
        })}${
          !isCampaignLoading && displayDiscountPercent && displayComparePrice
            ? t('sr.discount_part', {
                discount: displayDiscountPercent,
                comparePrice: formatPrice(displayComparePrice),
              })
            : ''
        }${productData?.note ? t('sr.note_prefix', { note: productData.note }) : ''}`}
      </span>
    </figcaption>
  )
}

/**
 * Component hiển thị tiêu đề sản phẩm với link và tracking.
 */
const ProductTitleLink = ({
  href,
  title,
  onClick,
  gaTrackingValue,
  gaTrackingLabel,
  disableNavigation,
}: {
  href?: string
  title?: string
  onClick: () => void
  gaTrackingValue?: string
  gaTrackingLabel?: string
  disableNavigation?: boolean
}) => {
  // const productSlug = productHref.split('/').pop() || productHref

  if (disableNavigation) {
    return (
      <h3 className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 lg:text-base lg:leading-5'>
        {title}
      </h3>
    )
  }

  return (
    <Link
      href={href || '/'}
      className='w-fit'
      aria-label={`Xem chi tiết sản phẩm ${title}`}
      onClick={onClick}
      prefetch={false}
      {...(gaTrackingValue
        ? {
            'ga-tracking-value': gaTrackingValue,
            ...(gaTrackingLabel && {
              'ga-tracking-label': gaTrackingLabel,
            }),
          }
        : {})}
    >
      <h3 className='line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 hover:text-primary lg:text-base lg:leading-5'>
        {title}
      </h3>
    </Link>
  )
}

/**
 * Block hiển thị giá, phần trăm giảm, giá gạch ngang, kèm skeleton khi loading.
 */
const ProductPriceBlock = ({
  isLoading,
  isCoolClubSale,
  displayPrice,
  displayDiscountPercent,
  displayComparePrice,
}: {
  isLoading: boolean
  isCoolClubSale: boolean
  displayPrice: number
  displayDiscountPercent?: number
  displayComparePrice?: number
}) => (
  <div className='flex flex-wrap items-center gap-1'>
    {isCoolClubSale && (
      <Image
        src='/icons/icon-price-signin.png'
        alt='Biểu tượng giá ưu đãi khi đăng nhập'
        width={16}
        height={16}
        loading='lazy'
        decoding='async'
      />
    )}
    {isLoading ? (
      <>
        <Skeleton className='h-4 w-16 rounded bg-neutral-200' />
        <Skeleton className='h-4 w-10 rounded bg-neutral-200' />
      </>
    ) : (
      <>
        <p
          className={cn(
            'font-sans text-xs font-bold leading-3.5 text-neutral-900 lg:text-base lg:leading-5',
            isCoolClubSale && 'text-primary'
          )}
        >
          {formatPrice(displayPrice)}
        </p>
        {displayDiscountPercent ? (
          <TagThumbnail
            title={`-${displayDiscountPercent}%`}
            variant='primary'
            size='small'
            displayDiscountPercent={displayDiscountPercent}
            className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
          />
        ) : null}
        {displayComparePrice ? (
          <p className='text-xs leading-3.5 text-neutral-500 line-through lg:text-sm lg:leading-4.5'>
            {formatPrice(displayComparePrice)}
          </p>
        ) : null}
      </>
    )}
  </div>
)

/**
 * Component hiển thị label giá CoolClub dựa trên số lượng rankings
 * - Nếu có đủ 4 rankings → hiển thị "Giá Coolclub"
 * - Nếu < 4 rankings → hiển thị rank thấp nhất: "Giá TV mới", "Giá hạng bạc", "Giá hạng vàng", "Giá hạng bạch kim"
 */
const CoolClubPriceLabel = ({
  rankings,
  price,
}: {
  rankings?: string[]
  price: number
}) => {
  const t = useTranslations('product_detail.title')

  const labelText = useMemo(() => {
    if (!rankings || rankings.length === 0) {
      return t('coolclub_price_label')
    }

    // Nếu có đủ 4 rankings → hiển thị "Giá Coolclub"
    if (rankings.length === 4) {
      return t('coolclub_price_label')
    }

    // Tìm rank thấp nhất theo thứ tự: NEW -> SILVER -> GOLD -> PLATINUM
    for (const rankId of RANKING_ORDER) {
      if (rankings.includes(rankId)) {
        const label = RANKING_ID_TO_LABEL[rankId]
        return label
          ? t(`coolclub_price_by_rank.${label}`)
          : t('coolclub_price_label')
      }
    }

    return t('coolclub_price_label')
  }, [rankings, t])

  return (
    <p className='line-clamp-1 text-sm text-primary-500'>
      {labelText}: {formatPrice(price)}
    </p>
  )
}
